import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import { templatesModel } from '@/entities/templates';
import {
  normalizeContentLanguage,
  resolveContentLanguage,
  TRANSCRIPTOR_LANGUAGE,
} from '@/entities/transcriptor';

import { filterTemplateDataByLanguage } from '@/features/templateList';

export const useLocalizedTemplates = () => {
  const { locale } = useIntl();

  const isConsultationTemplatesEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_ConsultationTemplates,
    ),
  );

  const schemas = useAppSelector(
    templatesModel.selectors.selectPublishedEncounterSchemas,
  );
  const documentTemplates = useAppSelector(
    templatesModel.selectors.selectActiveDocumentTemplates,
  );

  const requestedLanguage = isConsultationTemplatesEnabled
    ? normalizeContentLanguage(locale)
    : resolveContentLanguage(locale);

  return useMemo(() => {
    const localized = filterTemplateDataByLanguage({
      language: requestedLanguage,
      schemas,
      templates: documentTemplates,
    });

    const shouldFallBackToEnglish =
      localized.schemas.length === 0 &&
      requestedLanguage !== TRANSCRIPTOR_LANGUAGE.English;

    if (!shouldFallBackToEnglish) {
      return {
        contentLanguage: requestedLanguage,
        schemas: localized.schemas,
        documentTemplates: localized.templates,
      };
    }

    const fallback = filterTemplateDataByLanguage({
      language: TRANSCRIPTOR_LANGUAGE.English,
      schemas,
      templates: documentTemplates,
    });

    return {
      contentLanguage: TRANSCRIPTOR_LANGUAGE.English,
      schemas: fallback.schemas,
      documentTemplates: fallback.templates,
    };
  }, [requestedLanguage, schemas, documentTemplates]);
};
